// --- React
import { useCallback, useMemo, useState } from "react";
// --- Config & Utils
import * as columnDefs from "../../../../../components/AGGrid/config/columns-def";
// --- Hooks
import { useWindowsData } from "../../../hooks/useWindowsData";

/**
 * Custom hook for Windows grid utilities.
 *
 * Features:
 * - Manages sorting state and logic for the Windows grid
 * - Fetches sorted windows data from the API via useWindowsData
 * - Prepares column definitions with sorting controls
 *
 * @param {object} params - Optional configuration
 * @param {Array} params.columnHandlers - Handlers to pass to column definitions
 * @param {boolean} params.isTapAdmin - Whether the current user has TAP Admin permissions
 * @returns {object} { setSort, columns }
 */
export function useWindowsGridUtils({
  columnHandlers = [],
  isTapAdmin = false,
} = {}) {
  const { fetchWindows, updateWindows, windowsRange } = useWindowsData();
  const [sortedColId, setSortedColId] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  // Handle sorting: update state, fetch sorted data, update Windows
  const setSort = useCallback(
    async (colId, direction) => {
      setSortedColId(colId);
      setSortDirection(direction);

      const customSort = [colId, direction.toUpperCase()];
      const data = await fetchWindows(windowsRange, null, customSort);

      if (data) {
        updateWindows({
          windowSequences: data?.content,
          totalPages: data?.totalPages,
          totalItems: data?.totalElements,
          currentPage: data?.pageable?.pageNumber,
          windowsSort: customSort,
        });
      }
    },
    [fetchWindows, updateWindows, windowsRange],
  );

  // Prepare column definitions with sorting controls
  const baseColumns = columnDefs["windows"](...columnHandlers, isTapAdmin)[
    "default"
  ];

  const columns = useMemo(
    () =>
      baseColumns.map((col) => ({
        ...col,
        headerComponentParams: {
          ...col.headerComponentParams,
          setSort,
          sortedColId,
          sortDirection,
        },
      })),
    [setSort, sortedColId, sortDirection, baseColumns],
  );

  return { setSort, columns };
}
